import { useEffect } from 'react';
import type { DectResponse } from 'src/apis/contract-management/types';
import { isServerError, useAxios } from 'src/hooks/use-axios';

type HookConfig = {
  url: string;
};

type ExecuteProps = {
  url: string;
};

export function useDect({ url }: HookConfig) {
  const request = useAxios(
    (axios, baseConfig, { url }: ExecuteProps) =>
      axios.request<DectResponse>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    request.execute({ url });
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  return request;
}

export type DectRequest = ReturnType<typeof useDect>;

type OptionalHookConfig = {
  url?: string;
};

export function useOptionalDect({ url }: OptionalHookConfig) {
  const request = useAxios(
    (axios, baseConfig, { url }: ExecuteProps) =>
      axios.request<DectResponse>({
        ...baseConfig,
        url,
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        // workaround for https://prisma.atlassian.net/browse/TAG-8049
        if (isServerError(error, 403)) {
          return error;
        } else {
          throw error;
        }
      },
    }
  );

  useEffect(() => {
    if (!url) return;
    request.execute({ url });
    // oxlint-disable-next-line exhaustive-deps
  }, [url]);

  if (!url) return;
  return request;
}

export type OptionalDectRequest = ReturnType<typeof useOptionalDect>;
